
import React, { useEffect, useRef, useState } from "react";
import loader from "../Assets/spinner.gif";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "./product.css";
import { FaArrowRight, FaTrash } from "react-icons/fa";
import { BsBoxSeam } from "react-icons/bs";
import { AiFillHeart, AiOutlineHeart } from "react-icons/ai";
import { FiPlusCircle, FiMinus, FiPlus } from "react-icons/fi";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import {
  addToBuyCart,
  decreaseBuyQty,
  addToRentCart,
  decreaseRentQty,
} from "../../redux/cartSlice";
import { addWishlistItem, removeWishlistItem } from "../../redux/wishlistSlice";
import { generateUUID } from "../../utils/RandomId";
import axiosConfig from "../../Services/axiosConfig";
import LoginModal from "../Login/Login";
import { toast } from "react-toastify";

/* ─────────────────── Image Slider ─────────────────── */
function ImageSlider({ images, product }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const intervalRef = useRef(null);

  function startSlide() {
    if (images.length <= 1) return;
    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % images.length);
    }, 1200);
  }

  function stopSlide() {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    setActiveIndex(0);
  }

  return (
    <div
      className="image-slide"
      onMouseEnter={startSlide}
      onMouseLeave={stopSlide}
    >
      {images.map((im, index) => (
        <img
          key={index}
          src={im?.image?.image}
          alt={im?.image?.name || product.name}
          className={`slide-image ${index === activeIndex ? "active" : ""}`}
        />
      ))}
    </div>
  );
}

/* ─────────────────── Single Product Card ─────────────────── */
function ProductCard({ product, listingType, onNavigate, onRemove, onRefresh, isWishlistPage }) {
  const dispatch = useDispatch();
  const buyCart = useSelector((s) => s.cart.buyCart);
  const rentCart = useSelector((s) => s.cart.rentCart);

  // Wishlist
  const [wishListed, setWishListed] = useState(null);
  const [loginShow, setLoginShow] = useState(false);
  const [userId, setUserId] = useState(localStorage.getItem("userid"));
  const [isLogin, setIsLogin] = useState(localStorage.getItem("token"));

  useEffect(() => {
    const handleAuthChange = () => {
      setUserId(localStorage.getItem("userid"));
      setIsLogin(localStorage.getItem("token"));
    };
    window.addEventListener("auth-changed", handleAuthChange);
    window.addEventListener("storage", handleAuthChange);
    return () => {
      window.removeEventListener("auth-changed", handleAuthChange);
      window.removeEventListener("storage", handleAuthChange);
    };
  }, []);

  // Rental date picker popup
  const [showRentPicker, setShowRentPicker] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const pickerRef = useRef(null);

  // ── Cart lookup ──
  const buyCartItem = buyCart.find((i) => i.id === product.id);
  const rentCartItem = rentCart.find((i) => i.id === product.id); // first match
  const isRental = listingType === "rental";
  const cartItem = isRental ? rentCartItem : buyCartItem;
  const cartQty = cartItem?.qty || 0;

  // ── Wishlist fetch ──
  useEffect(() => {
    if (!userId || !product.id) return;
    const type = listingType === "sale" ? "buy" : "rent";
    axiosConfig
      .get(`/catlog/wishlist-check/?variant_id=${product.id}&type=${type}`)
      .then((res) => setWishListed(res?.data))
      .catch(() => { });
  }, [product.id, listingType, userId]);

  // ── Close rental picker on outside click ──
  useEffect(() => {
    function handleOutside(e) {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowRentPicker(false);
      }
    }
    if (showRentPicker) document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [showRentPicker]);

  function formatLocalDate(date) {
    if (!date) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  // ── Wishlist handlers ──
  async function toggleWishlist(uid) {
    try {
      if (wishListed?.in_wishlist) {
        const deleteId = product.wishlistId || wishListed.id;
        await axiosConfig.delete(`/catlog/wishlists/${deleteId}/`);
        toast.success("Removed from wishlist");
        setWishListed((prev) => ({ ...prev, in_wishlist: false }));
        dispatch(removeWishlistItem(product.id));
        if (onRefresh) onRefresh();
      } else {
        const type = listingType === "sale" ? "sale" : "rental";
        await axiosConfig.post(`/catlog/wishlists/`, {
          user: uid,
          varient: product.id,
          type,
        });
        toast.success("Added to wishlist");
        // re-fetch to get the id & wishlist entry data
        const wType = listingType === "sale" ? "buy" : "rent";
        const res = await axiosConfig.get(
          `/catlog/wishlist-check/?variant_id=${product.id}&type=${wType}`
        );
        setWishListed(res?.data);
        dispatch(addWishlistItem({ id: product.id, type, wishlistId: res?.data?.id }));
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      console.error(err);
    }
  }

  async function handleWishlist(e) {
    e.stopPropagation();
    if (!isLogin) return setLoginShow(true);
    toggleWishlist(userId);
  }

  // ── Cart: initial add ──
  function handleAdd(e) {
    e.stopPropagation();
    if (isRental) {
      const urlType = listingType === "rental" ? "rent" : "buy";

      onNavigate({
        ...product,
        listingType: urlType,
      });

      return;
    }
    else {
      dispatch(
        addToBuyCart({
          id: product.id,
          name: product.name,
          friendlyurl: product.slug,
          type: "buy",
          image: product.images?.[0]?.image?.image,
          oldPrice: product.prices?.sale_price,
          offerPrice:
            product.prices?.sale_offer_price || product.prices?.sale_price,
        })
      );
      toast.success("Added to cart");
    }
  }

  // ── Cart: confirm rental add ──
  function handleConfirmRent(e) {
    e.stopPropagation();
    if (!fromDate) {
      toast.error("Please select a start date");
      return;
    }
    const cartItemId = generateUUID();
    dispatch(
      addToRentCart({
        id: product.id,
        name: product.name,
        friendlyurl: product.slug,
        cartItemId,
        type: "rent",
        image: product.images?.[0]?.image?.image,
        offerPrice: product.prices?.rental_price,
        rentPerDay: product.prices?.rental_price,
        fromDate: formatLocalDate(fromDate),
      })
    );
    toast.success("Added to cart");
    setFromDate(null);
    setShowRentPicker(false);
  }

  // ── Cart: increase existing qty ──
  function handleIncrease(e) {
    e.stopPropagation();
    if (isRental && rentCartItem) {
      dispatch(addToRentCart({ ...rentCartItem }));
    } else if (!isRental) {
      dispatch(
        addToBuyCart({
          id: product.id,
          name: product.name,
          friendlyurl: product.slug,
          type: "buy",
          image: product.images?.[0]?.image?.image,
          oldPrice: product.prices?.sale_price,
          offerPrice:
            product.prices?.sale_offer_price || product.prices?.sale_price,
        })
      );
    }
  }

  // ── Cart: decrease / remove ──
  function handleDecrease(e) {
    e.stopPropagation();
    if (isRental && rentCartItem) {
      dispatch(decreaseRentQty(rentCartItem.cartItemId));
    } else if (!isRental) {
      dispatch(decreaseBuyQty(product.id));
    }
  }

  const formatPrice = (price) =>
    price
      ?.toLocaleString("en-US", { style: "currency", currency: "USD" })
      .replace("$", "$ ");

  return (
    <div className="product-card" onClick={() => onNavigate(product)}>
      {/* ── Image + overlays ── */}
      <div className="card-image-wrapper">
        <ImageSlider images={product.images || []} product={product} />

        {/* Wishlist — top right */}
        <button
          className="card-wishlist-btn"
          onClick={handleWishlist}
          title={wishListed?.in_wishlist ? "Remove from wishlist" : "Add to wishlist"}
        >
          {wishListed?.in_wishlist ? (
            <AiFillHeart className="card-heart-filled" />
          ) : (
            <AiOutlineHeart className="card-heart-outline" />
          )}
        </button>


      </div>

      {/* ── Card body ── */}
      <div className="product-card-body">
        <h4 className="product-name">{product.name}</h4>

        {product.options && product.options.length > 0 && (
          <div className="product-options-grid">
            {product.options.map((opt) => (
              <div className="product-option-item" key={opt.id}>
                <div className="option-text">
                  <span className="option-label">{opt.variant_type}:</span>
                  <span className="option-value">{opt.variant_option}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="product-price-box">
          <span className="product-tag">
            {listingType === "buy/rent" ? "buy / rent" : listingType}
          </span>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: "4px" }}>
            <div style={{ flex: 1 }}>
              <div className="product-price-row" style={{ marginTop: 0 }}>
                {listingType === "sale" && (
                  <>
                    {Number(product.prices?.sale_offer_price) > 0 ? (
                      <div className="price-row">
                        <span className="offer-price">
                          {formatPrice(product.prices.sale_offer_price)}
                        </span>
                        <span className="product-old-price">
                          {formatPrice(product.prices.sale_price)}
                        </span>
                        <span className="discount-badge">
                          {parseFloat((
                            ((product.prices.sale_price -
                              product.prices.sale_offer_price) /
                              product.prices.sale_price) *
                            100
                          ).toFixed(2))}
                          % OFF
                        </span>
                      </div>
                    ) : (
                      <div className="price-row">
                        <span className="offer-price">
                          {formatPrice(product.prices.sale_price)}
                        </span>
                      </div>
                    )}
                  </>
                )}

                {listingType === "rental" && product.prices?.rental_price && (
                  <div className="price-row">
                    <span className="offer-price">
                      {formatPrice(
                        Number(product.prices?.rental_offer_price) > 0 &&
                          Number(product.prices?.rental_offer_price) < Number(product.prices?.rental_price)
                          ? product.prices.rental_offer_price
                          : product.prices.rental_price
                      )}{" "}
                      / Day
                    </span>

                    {Number(product.prices?.rental_offer_price) > 0 &&
                      Number(product.prices?.rental_offer_price) < Number(product.prices?.rental_price) && (
                        <span className="product-old-price">
                          {formatPrice(product.prices.rental_price)}
                        </span>
                      )}
                  </div>
                )}

                {listingType === "buy/rent" && (
                  <div className="price-row dual-price-row">
                    <span className="offer-price">
                      {formatPrice(
                        Number(product.prices?.sale_offer_price) > 0
                          ? product.prices?.sale_offer_price
                          : product.prices?.sale_price
                      )}
                    </span>
                    {product.prices?.rental_price && (
                      <span className="dual-rental-price">
                        {formatPrice(
                          Number(product.prices?.rental_offer_price) > 0 &&
                            Number(product.prices?.rental_offer_price) < Number(product.prices?.rental_price)
                            ? product.prices.rental_offer_price
                            : product.prices.rental_price
                        )}{" "}
                        / Day

                        {Number(product.prices?.rental_offer_price) > 0 &&
                          Number(product.prices?.rental_offer_price) < Number(product.prices?.rental_price) && (
                            <span className="product-old-price" style={{ marginLeft: 6 }}>
                              {formatPrice(product.prices.rental_price)}
                            </span>
                          )}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {listingType === "rental" ? (
                product.rental_stock === 0 ? (
                  <div className="stock-error">Out of stock</div>
                ) : product.is_low_stock === "lowstock" ? (
                  <div className="stock-error">
                    Only {product.rental_stock} stock available
                  </div>
                ) : null
              ) : product.sale_stock === 0 ? (
                <div className="stock-error">Out of stock</div>
              ) : product.is_low_stock === "lowstock" ? (
                <div className="stock-error">
                  Only {product.sale_stock} stock available
                </div>
              ) : null}

            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "10px", paddingBottom: "2px" }}>
              <div className="hover-icon">
                {isWishlistPage && (
                  <FaTrash
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onRemove) onRemove(product.wishlistId);
                      dispatch(removeWishlistItem(product.id));
                    }}
                    className="delete-icon"
                  />
                )}
              </div>
              <div className="card-cart-area" onClick={(e) => e.stopPropagation()} ref={pickerRef}>
                {cartQty === 0 || isRental ? (
                  <button className="card-add-btn" onClick={handleAdd} title="Add to cart">
                    <FiPlusCircle size={26} />
                  </button>
                ) : (
                  <div className="card-qty-stepper">
                    <button className="card-qty-btn" onClick={handleDecrease}>
                      <FiMinus size={13} />
                    </button>
                    <span className="card-qty-num">{cartQty}</span>
                    <button className="card-qty-btn" onClick={handleIncrease}>
                      <FiPlus size={13} />
                    </button>
                  </div>
                )}
               
              </div>
            </div>
          </div>
        </div>
      </div>

      <div onClick={(e) => e.stopPropagation()}>
        <LoginModal show={loginShow} onHide={() => setLoginShow(false)} onLoginSuccess={(uid) => toggleWishlist(uid)} />
      </div>
    </div>
  );
}

/* ─────────────────── ProductSection ─────────────────── */
function ProductSection({ products = [], loading = false, searchListingType, onRefresh }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { categoryurl } = useParams();

  function getListingType(product) {
    if (location.pathname.includes("/search/results")) {
      if (searchListingType === "buy") return "sale";
      if (searchListingType === "rent") return "rental";
      if (product.varient_listing_type === "buy/rent") return "buy/rent";
      if (product.varient_listing_type === "buy") return "sale";
      if (product.varient_listing_type === "rent") return "rental";
    }
    if (product.wishlistType) {
      return product.wishlistType === "sale" ? "sale" : "rental";
    }
    if (categoryurl === "buy") return "sale";
    if (categoryurl === "rent") return "rental";
    return "rental";
  }

  function handleNavigate(product) {
    const listingType = getListingType(product);
    const urlType = listingType === "rental" ? "rent" : "buy";
    navigate(`/${urlType}/product/${product.slug}`, {
      state: { item: product, listingType: urlType },
    });
  }

  async function removeProduct(id) {
    try {
      await axiosConfig.delete(`/catlog/wishlists/${id}/`);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.log(error);
    }
  }

  if (loading) return <img src={loader} alt="loading" />;

  if (!loading && products.length === 0)
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "80px 20px",
          width: "100%",
          gridColumn: "1 / -1",
        }}
      >
        <div style={{ fontSize: "64px", marginBottom: "16px" }}>
          <BsBoxSeam
            style={{ fontSize: "64px", color: "#ccc", marginBottom: "16px" }}
          />
        </div>
        <h3 style={{ fontSize: "20px", fontWeight: "600", margin: "0 0 8px", color: "#1a1a1a" }}>
          No products found
        </h3>
        <p
          style={{
            fontSize: "14px",
            color: "#888",
            margin: 0,
            textAlign: "center",
            maxWidth: "280px",
          }}
        >
          Try adjusting your filters or search with a different keyword.
        </p>
      </div>
    );

  const isWishlistPage = location.pathname.includes("/wishlist");

  return (
    <div className="products">
      {products.map((product) => {
        const listingType = getListingType(product);
        return (
          <ProductCard
            key={product.id}
            product={product}
            listingType={listingType}
            onNavigate={handleNavigate}
            onRemove={removeProduct}
            onRefresh={onRefresh}
            isWishlistPage={isWishlistPage}
          />
        );
      })}
    </div>
  );
}

export default ProductSection;
